/**
 * useLiquiditaet — Hook für Liquiditätsplanung & Kontenmanagement
 * 
 * Funktionen:
 * - Konten CRUD (Geschäftskonten, Barkassen, Sparkonten, Anlagen, Privat)
 * - Transaktionen CRUD (Ist-Buchungen + Plan-Buchungen)
 * - Kategorien laden
 * - Aggregationen: Cashflow pro Monat, Saldo-Verlauf, konsolidiert/einzeln
 * - Planposten für Prognose
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ============================================================
// TYPEN
// ============================================================

export interface FinanzKonto {
  id: string;
  name: string;
  typ: 'geschaeftskonto' | 'barkasse' | 'sparkonto' | 'anlage' | 'kreditkarte' | 'privatkonto';
  firma: string | null;
  bank_name: string | null;
  iban: string | null;
  bic: string | null;
  waehrung: string;
  aktueller_saldo: number;
  saldo_datum: string;
  ist_aktiv: boolean;
  farbe: string;
  icon: string;
  sortierung: number;
  notizen: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanzTransaktion {
  id: string;
  konto_id: string;
  kategorie_id: string | null;
  datum: string;
  betrag: number;
  beschreibung: string | null;
  empfaenger_absender: string | null;
  referenz: string | null;
  typ: 'ist' | 'plan' | 'wiederkehrend';
  ist_abgeglichen: boolean;
  beleg_url: string | null;
  tags: string[] | null;
  firma: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanzKategorie {
  id: string;
  name: string;
  typ: 'einnahme' | 'ausgabe';
  parent_id: string | null;
  farbe: string;
  icon: string | null;
  ist_standard: boolean;
  sortierung: number;
}

export interface FinanzPlanposten {
  id: string;
  konto_id: string;
  kategorie_id: string | null;
  name: string;
  betrag: number;
  typ: 'einnahme' | 'ausgabe';
  rhythmus: 'einmalig' | 'woechentlich' | 'monatlich' | 'quartalsweise' | 'jaehrlich';
  start_datum: string;
  end_datum: string | null;
  naechstes_datum: string | null;
  firma: string | null;
  ist_aktiv: boolean;
  notizen: string | null;
}

export interface MonatsCashflow {
  monat: string; // YYYY-MM
  label: string; // "Jan 2026"
  einnahmen: number;
  ausgaben: number;
  saldo: number;
  endbestand: number;
}

export interface FirmaInfo {
  id: string;
  name: string;
  farbe: string;
}

// Firmen-Mapping
export const FIRMEN: Record<string, FirmaInfo> = {
  sv_buero_koop: { id: 'sv_buero_koop', name: 'SV Büro Koop', farbe: '#2563EB' },
  lcs_gmbh: { id: 'lcs_gmbh', name: 'LCS GmbH', farbe: '#7C3AED' },
  prolea_fzco: { id: 'prolea_fzco', name: 'PROLEA FZCO', farbe: '#0EA5E9' },
  unfallgiganten: { id: 'unfallgiganten', name: 'Unfallgiganten', farbe: '#DC2626' },
  mietgo: { id: 'mietgo', name: 'MietGo', farbe: '#D97706' },
  privat: { id: 'privat', name: 'Privat', farbe: '#6366F1' },
};

export const KONTO_TYPEN: Record<string, { label: string; farbe: string }> = {
  geschaeftskonto: { label: 'Geschäftskonto', farbe: '#2563EB' },
  barkasse: { label: 'Barkasse', farbe: '#059669' },
  sparkonto: { label: 'Sparkonto', farbe: '#14B8A6' },
  anlage: { label: 'Anlage', farbe: '#F59E0B' },
  kreditkarte: { label: 'Kreditkarte', farbe: '#EF4444' },
  privatkonto: { label: 'Privatkonto', farbe: '#6366F1' },
};

// ============================================================
// HOOK: useKonten
// ============================================================

export function useKonten() {
  const [konten, setKonten] = useState<FinanzKonto[]>([]);
  const [loading, setLoading] = useState(true);

  const loadKonten = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('finanz_konten')
      .select('*')
      .order('sortierung', { ascending: true });
    if (!error && data) setKonten(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadKonten(); }, [loadKonten]);

  const createKonto = async (konto: Partial<FinanzKonto>) => {
    const { data, error } = await supabase
      .from('finanz_konten')
      .insert(konto)
      .select()
      .single();
    if (!error) await loadKonten();
    return { data, error };
  };

  const updateKonto = async (id: string, updates: Partial<FinanzKonto>) => {
    const { error } = await supabase
      .from('finanz_konten')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await loadKonten();
    return { error };
  };

  const deleteKonto = async (id: string) => {
    const { error } = await supabase
      .from('finanz_konten')
      .delete()
      .eq('id', id);
    if (!error) await loadKonten();
    return { error };
  };

  // Aggregationen
  const gesamtSaldo = konten.filter(k => k.ist_aktiv).reduce((sum, k) => sum + Number(k.aktueller_saldo), 0);
  const kontenNachTyp = konten.reduce((acc, k) => {
    if (!acc[k.typ]) acc[k.typ] = [];
    acc[k.typ].push(k);
    return acc;
  }, {} as Record<string, FinanzKonto[]>);
  const kontenNachFirma = konten.reduce((acc, k) => {
    const firma = k.firma || 'sonstige';
    if (!acc[firma]) acc[firma] = [];
    acc[firma].push(k);
    return acc;
  }, {} as Record<string, FinanzKonto[]>);

  return {
    konten, loading, loadKonten,
    createKonto, updateKonto, deleteKonto,
    gesamtSaldo, kontenNachTyp, kontenNachFirma,
  };
}

// ============================================================
// HOOK: useTransaktionen
// ============================================================

export function useTransaktionen(kontoId?: string, firma?: string) {
  const [transaktionen, setTransaktionen] = useState<FinanzTransaktion[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTransaktionen = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('finanz_transaktionen')
      .select('*')
      .order('datum', { ascending: false })
      .limit(500);
    if (kontoId) query = query.eq('konto_id', kontoId);
    if (firma) query = query.eq('firma', firma);
    const { data, error } = await query;
    if (!error && data) setTransaktionen(data);
    setLoading(false);
  }, [kontoId, firma]);

  useEffect(() => { loadTransaktionen(); }, [loadTransaktionen]);

  const createTransaktion = async (t: Partial<FinanzTransaktion>) => {
    const { data, error } = await supabase
      .from('finanz_transaktionen')
      .insert(t)
      .select()
      .single();
    if (!error) await loadTransaktionen();
    return { data, error };
  };

  const updateTransaktion = async (id: string, updates: Partial<FinanzTransaktion>) => {
    const { error } = await supabase
      .from('finanz_transaktionen')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await loadTransaktionen();
    return { error };
  };

  const deleteTransaktion = async (id: string) => {
    const { error } = await supabase
      .from('finanz_transaktionen')
      .delete()
      .eq('id', id);
    if (!error) await loadTransaktionen();
    return { error };
  };

  // Einnahmen/Ausgaben berechnen
  const einnahmen = transaktionen.filter(t => t.betrag > 0).reduce((s, t) => s + Number(t.betrag), 0);
  const ausgaben = transaktionen.filter(t => t.betrag < 0).reduce((s, t) => s + Math.abs(Number(t.betrag)), 0);

  return {
    transaktionen, loading, loadTransaktionen,
    createTransaktion, updateTransaktion, deleteTransaktion,
    einnahmen, ausgaben,
  };
}

// ============================================================
// HOOK: useKategorien
// ============================================================

export function useKategorien() {
  const [kategorien, setKategorien] = useState<FinanzKategorie[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('finanz_kategorien')
        .select('*')
        .order('sortierung', { ascending: true });
      if (!error && data) setKategorien(data);
      setLoading(false);
    })();
  }, []);

  const einnahmeKategorien = kategorien.filter(k => k.typ === 'einnahme');
  const ausgabeKategorien = kategorien.filter(k => k.typ === 'ausgabe');

  return { kategorien, loading, einnahmeKategorien, ausgabeKategorien };
}

// ============================================================
// HOOK: usePlanposten
// ============================================================

export function usePlanposten(firma?: string) {
  const [planposten, setPlanposten] = useState<FinanzPlanposten[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPlanposten = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('finanz_planposten')
      .select('*')
      .eq('ist_aktiv', true)
      .order('start_datum', { ascending: true });
    if (firma) query = query.eq('firma', firma);
    const { data, error } = await query;
    if (!error && data) setPlanposten(data);
    setLoading(false);
  }, [firma]);

  useEffect(() => { loadPlanposten(); }, [loadPlanposten]);

  const createPlanposten = async (p: Partial<FinanzPlanposten>) => {
    const { data, error } = await supabase
      .from('finanz_planposten')
      .insert(p)
      .select()
      .single();
    if (!error) await loadPlanposten();
    return { data, error };
  };

  const updatePlanposten = async (id: string, updates: Partial<FinanzPlanposten>) => {
    const { error } = await supabase
      .from('finanz_planposten')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await loadPlanposten();
    return { error };
  };

  const deletePlanposten = async (id: string) => {
    const { error } = await supabase
      .from('finanz_planposten')
      .delete()
      .eq('id', id);
    if (!error) await loadPlanposten();
    return { error };
  };

  return { planposten, loading, loadPlanposten, createPlanposten, updatePlanposten, deletePlanposten };
}

// ============================================================
// HELPER: Cashflow pro Monat berechnen
// ============================================================

export function berechneCashflowProMonat(
  transaktionen: FinanzTransaktion[],
  startSaldo: number,
  monate: number = 6
): MonatsCashflow[] {
  const now = new Date();
  const result: MonatsCashflow[] = [];
  let laufenderSaldo = startSaldo;

  for (let i = monate - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monatKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });

    const monatsTx = transaktionen.filter(t => t.datum.startsWith(monatKey));
    const einnahmen = monatsTx.filter(t => Number(t.betrag) > 0).reduce((s, t) => s + Number(t.betrag), 0);
    const ausgaben = monatsTx.filter(t => Number(t.betrag) < 0).reduce((s, t) => s + Math.abs(Number(t.betrag)), 0);
    const saldo = einnahmen - ausgaben;
    laufenderSaldo += saldo;

    result.push({ monat: monatKey, label, einnahmen, ausgaben, saldo, endbestand: laufenderSaldo });
  }

  return result;
}

// ============================================================
// HELPER: Währung formatieren
// ============================================================

export function formatBetrag(betrag: number, waehrung: string = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: waehrung,
    minimumFractionDigits: 2,
  }).format(betrag);
}
